import { API, API_BASE } from '../../config/api';
import { useState, useRef } from 'react';
import { api } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '../ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, MoreVertical, Upload, Loader2, ShoppingBag, Image } from 'lucide-react';

const CATEGORIES = ['apparel', 'accessories', 'prints', 'other'];

export function MerchTab({ merch, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const imageRef = useRef(null);
  const [form, setForm] = useState({
    title: '', description: '', price: 0, image: '', category: 'apparel',
    sizes: [], in_stock: true, external_url: '', sale_price: '', on_sale: false, purchasable: false
  });

  const resetForm = () => {
    setForm({ title: '', description: '', price: 0, image: '', category: 'apparel', sizes: [], in_stock: true, external_url: '', sale_price: '', on_sale: false, purchasable: false });
    setEditing(null);
  };

  const openEdit = (item) => {
    setForm({
      title: item.title || '', description: item.description || '', price: item.price || 0,
      image: item.image || '', category: item.category || 'apparel',
      sizes: item.sizes || [], in_stock: item.in_stock !== false, external_url: item.external_url || '',
      sale_price: item.sale_price || '', on_sale: item.on_sale || false, purchasable: item.purchasable || false
    });
    setEditing(item);
    setDialogOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`${API}/upload/cover`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, image: `${API_BASE}${data.url}` }));
      toast.success('Image uploaded!');
    } catch (err) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      sale_price: form.sale_price !== '' && form.sale_price != null ? parseFloat(form.sale_price) || null : null,
    };
    try {
      if (editing) {
        await api.put(`${API}/merch/${editing.id}`, payload);
        toast.success('Merch item updated!');
      } else {
        await api.post(`${API}/merch`, payload);
        toast.success('Merch item created!');
      }
      setDialogOpen(false);
      resetForm();
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ') : 'Failed to save merch item';
      toast.error(msg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this merch item?')) return;
    try {
      await api.delete(`${API}/merch/${id}`);
      toast.success('Merch item deleted');
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const toggleSize = (size) => {
    setForm(prev => ({
      ...prev,
      sizes: prev.sizes.includes(size) ? prev.sizes.filter(s => s !== size) : [...prev.sizes, size]
    }));
  };

  const toggleStock = async (item) => {
    try {
      await api.put(`${API}/merch/${item.id}`, { ...item, in_stock: !item.in_stock });
      toast.success(item.in_stock ? 'Marked out of stock' : 'Marked in stock');
      onRefresh();
    } catch {
      toast.error('Failed to update stock');
    }
  };

  return (
    <div className="space-y-6" data-testid="merch-content">
      <div className="flex items-center justify-between">
        <h2 className="font-cabinet text-2xl font-black uppercase tracking-tighter">MERCH STORE</h2>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="add-merch-btn">
              <Plus className="w-4 h-4 mr-2" /> ADD ITEM
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#141414] border-[#262626] rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-cabinet text-xl font-black uppercase">
                {editing ? 'EDIT ITEM' : 'ADD MERCH ITEM'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="overline">Title</Label>
                <Input value={form.title} onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                  className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none" required data-testid="merch-title-input" />
              </div>
              <div>
                <Label className="overline">Description</Label>
                <textarea value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  className="mt-2 w-full bg-[#0A0A0A] border border-[#262626] p-3 text-white resize-none outline-none" rows={3}
                  data-testid="merch-description-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="overline">Price (USD)</Label>
                  <Input type="number" step="0.01" min="0" value={form.price}
                    onChange={(e) => setForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                    className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="merch-price-input" />
                </div>
                <div>
                  <Label className="overline">Category</Label>
                  <select value={form.category} onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
                    className="mt-2 w-full h-10 bg-[#0A0A0A] border border-[#262626] px-3 text-white" data-testid="merch-category-select">
                    {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label className="overline">Product Image</Label>
                <div className="mt-2 flex gap-2">
                  <Input value={form.image} onChange={(e) => setForm(prev => ({ ...prev, image: e.target.value }))}
                    className="flex-1 bg-[#0A0A0A] border-[#262626] rounded-none" placeholder="URL or upload" />
                  <input type="file" ref={imageRef} accept="image/*" onChange={handleImageUpload} className="hidden" />
                  <Button type="button" variant="outline" onClick={() => imageRef.current?.click()} disabled={uploading}
                    className="rounded-none border-[#262626] hover:border-[#FF3B30]" data-testid="upload-merch-image-btn">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  </Button>
                </div>
                {form.image && <div className="mt-2 w-20 h-20 bg-[#262626] overflow-hidden"><img src={form.image} alt="" className="w-full h-full object-cover" /></div>}
              </div>
              <div>
                <Label className="overline">Sizes (click to toggle)</Label>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {['XS', 'S', 'M', 'L', 'XL', 'XXL', 'One Size'].map(s => (
                    <button key={s} type="button" onClick={() => toggleSize(s)}
                      className={`px-3 py-1 border text-sm ${form.sizes.includes(s) ? 'bg-[#FF3B30] border-[#FF3B30] text-white' : 'border-[#262626] text-[#A3A3A3] hover:border-[#FF3B30]'}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="overline">External Store URL (optional)</Label>
                <Input value={form.external_url} onChange={(e) => setForm(prev => ({ ...prev, external_url: e.target.value }))}
                  className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none" placeholder="https://shopify.com/..." data-testid="merch-url-input" />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.in_stock} onCheckedChange={(checked) => setForm(prev => ({ ...prev, in_stock: checked }))} data-testid="merch-stock-switch" />
                <Label className="overline">In Stock</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.purchasable} onCheckedChange={(checked) => setForm(prev => ({ ...prev, purchasable: checked }))} data-testid="merch-purchasable-switch" />
                <div>
                  <Label className="overline">Available for Purchase</Label>
                  <p className="text-xs text-[#A3A3A3]">Show the Buy button. When off, item is visible but not buyable.</p>
                </div>
              </div>
              <div className="border-t border-[#262626] pt-4">
                <div className="flex items-center gap-3 mb-3">
                  <Switch checked={form.on_sale} onCheckedChange={(checked) => setForm(prev => ({ ...prev, on_sale: checked }))} data-testid="merch-sale-switch" />
                  <Label className="overline text-[#DAA520]">On Sale</Label>
                </div>
                {form.on_sale && (
                  <div>
                    <Label className="overline">Sale Price (USD)</Label>
                    <Input type="number" step="0.01" min="0" value={form.sale_price}
                      onChange={(e) => setForm(prev => ({ ...prev, sale_price: parseFloat(e.target.value) || 0 }))}
                      className="mt-2 bg-[#0A0A0A] border-[#DAA520]/30 rounded-none text-[#DAA520]" placeholder="e.g. 19.99" data-testid="merch-sale-price-input" />
                  </div>
                )}
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="merch-submit-btn">
                  {editing ? 'UPDATE' : 'ADD ITEM'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none border-[#262626]">CANCEL</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {merch.length === 0 ? (
        <div className="bg-[#141414] border border-[#262626] p-12 text-center">
          <ShoppingBag className="w-12 h-12 mx-auto text-[#A3A3A3] mb-4" />
          <p className="text-[#A3A3A3]">No merch items yet. Add your first product!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {merch.map(item => (
            <div key={item.id} className="bg-[#141414] border border-[#262626] overflow-hidden" data-testid={`merch-item-${item.id}`}>
              <div className="aspect-square bg-[#0A0A0A] overflow-hidden relative">
                {item.image ? (
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Image className="w-12 h-12 text-[#262626]" /></div>
                )}
                {item.on_sale && (
                  <div className="absolute top-2 left-2 bg-[#DAA520] px-2 py-0.5">
                    <span className="text-xs font-bold text-black uppercase">Sale</span>
                  </div>
                )}
                <button
                  onClick={async () => {
                    try {
                      const { data } = await api.put(`${API}/merch/home-featured/${item.id}`);
                      toast.success(data.featured ? `★ Featured on homepage (${data.count}/6)` : '☆ Removed from homepage');
                      if (onRefresh) onRefresh();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Toggle failed');
                    }
                  }}
                  title={item.home_featured ? `Featured on homepage (#${(item.home_featured_order ?? 0) + 1}/6) — click to remove` : 'Show on homepage (max 6)'}
                  className={`absolute top-2 right-2 w-9 h-9 flex items-center justify-center rounded-full backdrop-blur transition-colors text-lg leading-none ${item.home_featured ? 'bg-[#DAA520] text-black' : 'bg-black/60 text-white/70 hover:bg-[#DAA520]/30 hover:text-[#DAA520]'}`}
                  data-testid={`merch-home-feature-${item.id}`}
                >
                  {item.home_featured ? '★' : '☆'}
                </button>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold">{item.title}</h3>
                    {item.on_sale && item.sale_price != null ? (
                      <div className="flex items-center gap-2">
                        <span className="text-[#A3A3A3] font-mono line-through text-sm">${item.price.toFixed(2)}</span>
                        <span className="text-[#DAA520] font-mono font-bold">${item.sale_price.toFixed(2)}</span>
                      </div>
                    ) : (
                      <p className="text-[#FF3B30] font-mono">${item.price.toFixed(2)}</p>
                    )}
                    <p className="text-xs text-[#A3A3A3] mt-1">{item.category}</p>
                    {item.sizes?.length > 0 && <p className="text-xs text-[#A3A3A3]">{item.sizes.join(', ')}</p>}
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEdit(item)}
                      className="px-3 py-1.5 text-xs font-bold uppercase bg-[#00B4D8] hover:bg-[#00B4D8]/80 text-black transition-all"
                      data-testid={`merch-edit-${item.id}`}>
                      <Edit className="w-3.5 h-3.5 inline mr-1" />EDIT
                    </button>
                    <button onClick={() => handleDelete(item.id)}
                      className="px-3 py-1.5 text-xs font-bold uppercase bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white transition-all"
                      data-testid={`merch-delete-${item.id}`}>
                      <Trash2 className="w-3.5 h-3.5 inline mr-1" />DEL
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#262626]">
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-bold uppercase ${item.in_stock ? 'text-green-500' : 'text-red-500'}`}>
                      {item.in_stock ? 'In Stock' : 'Out of Stock'}
                    </span>
                    <span className={`text-[10px] font-bold uppercase ${item.purchasable ? 'text-[#22C55E]' : 'text-[#A3A3A3]'}`}>
                      {item.purchasable ? 'Purchasable' : 'Display Only'}
                    </span>
                  </div>
                  <Switch checked={item.in_stock !== false} onCheckedChange={() => toggleStock(item)} data-testid={`stock-toggle-${item.id}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
